import type { ChatMessage, ChatSuggestion, SuggestionChipState } from '@/types';

export function inThreadSuggestionMessageId(runId: string): string {
  return `suggestions_${runId}`;
}

/** Remove the in-thread suggestion row for a run (e.g. empty stop after skeleton preload). */
export function removeInThreadSuggestionRow(
  messages: ChatMessage[],
  runId: string,
): ChatMessage[] {
  const msgId = inThreadSuggestionMessageId(runId);
  return messages.filter((m) => m.id !== msgId);
}

function chipRichness(chips: SuggestionChipState[] | undefined): number {
  if (!chips?.length) return 0;
  return chips.reduce(
    (n, c) =>
      n +
      (c.label?.length ?? 0) +
      (c.message?.length ?? 0) +
      (c.clickable ? 10 : 0) +
      (c.id ? 1 : 0),
    0,
  );
}

/**
 * After provisional → real run_id: rename suggestion row id and merge if both rows exist.
 */
export function migrateInThreadSuggestionRunId(
  messages: ChatMessage[],
  previousRunId: string,
  newRunId: string,
): ChatMessage[] {
  if (!previousRunId || previousRunId === newRunId) return messages;

  const oldId = inThreadSuggestionMessageId(previousRunId);
  const newId = inThreadSuggestionMessageId(newRunId);
  const oldIdx = messages.findIndex((m) => m.id === oldId);
  if (oldIdx < 0) return messages;

  const oldRow = messages[oldIdx];
  const newIdx = messages.findIndex((m) => m.id === newId);

  if (newIdx < 0) {
    return messages.map((m, i) =>
      i === oldIdx ? { ...oldRow, id: newId, runId: newRunId } : m,
    );
  }

  const newRow = messages[newIdx];
  const pickChips =
    chipRichness(newRow.suggestionChips) >= chipRichness(oldRow.suggestionChips)
      ? newRow.suggestionChips
      : oldRow.suggestionChips;

  const { isDelta: _od, deltaType: _ot, ...oldBase } = oldRow as ChatMessage & {
    isDelta?: boolean;
    deltaType?: string;
  };
  const { isDelta: _nd, deltaType: _nt, ...newBase } = newRow as ChatMessage & {
    isDelta?: boolean;
    deltaType?: string;
  };

  const mergedRow: ChatMessage = {
    ...newBase,
    ...oldBase,
    id: newId,
    runId: newRunId,
    messageType: 'chat_suggestions',
    suggestionChips: pickChips ?? newRow.suggestionChips ?? oldRow.suggestionChips,
    suggestionsCursor: newRow.suggestionsCursor ?? oldRow.suggestionsCursor,
    finalizedAt: newRow.finalizedAt ?? oldRow.finalizedAt,
  };

  return messages.filter((m) => m.id !== oldId).map((m) => (m.id === newId ? mergedRow : m));
}

/** Keep a single in-thread suggestion row per runId (richest chips win). */
export function dedupeInThreadSuggestionRows(
  messages: ChatMessage[],
  runId: string,
): ChatMessage[] {
  const canonicalId = inThreadSuggestionMessageId(runId);
  const indices: number[] = [];
  messages.forEach((m, idx) => {
    if (m.messageType === 'chat_suggestions' && m.runId === runId) {
      indices.push(idx);
    }
  });
  if (indices.length <= 1) {
    const onlyIdx = indices[0];
    if (onlyIdx === undefined) return messages;
    if (messages[onlyIdx].id === canonicalId) return messages;
    return messages.map((m, idx) =>
      idx === onlyIdx ? { ...m, id: canonicalId } : m,
    );
  }

  let bestIdx = indices[0];
  let bestScore = chipRichness(messages[bestIdx].suggestionChips);
  for (const idx of indices.slice(1)) {
    const score = chipRichness(messages[idx].suggestionChips);
    if (score > bestScore) {
      bestScore = score;
      bestIdx = idx;
    }
  }

  const bestRow: ChatMessage = {
    ...messages[bestIdx],
    id: canonicalId,
    runId,
    messageType: 'chat_suggestions',
  };

  const insertAt = Math.min(...indices);
  const without = messages.filter(
    (m) => !(m.messageType === 'chat_suggestions' && m.runId === runId),
  );
  return [...without.slice(0, insertAt), bestRow, ...without.slice(insertAt)];
}

/** Prefer the longer of two streamed strings (belt-and-suspenders for truncated chip_ready). */
export function preferLongerText(
  current: string | undefined,
  incoming: string | undefined,
): string {
  const a = current || '';
  const b = incoming || '';
  return b.length > a.length ? b : a;
}

/** Merge block_stop payload suggestions into progressive chips without clobbering longer local text. */
export function mergeSuggestionChipsFromStop(
  chips: SuggestionChipState[],
  suggestions: ChatSuggestion[],
): SuggestionChipState[] {
  return chips.map((chip, idx) => {
    const fromPayload = suggestions[idx];
    if (!fromPayload) return chip;
    return {
      ...chip,
      id: chip.id || fromPayload.id,
      label: preferLongerText(chip.label, fromPayload.label),
      message: preferLongerText(chip.message, fromPayload.message),
      clickable: true,
      labelComplete: true,
      messageComplete: true,
    };
  });
}

/** Upsert an in-thread suggestion row directly below the assistant anchor. */
export function upsertSuggestionRowAfterAssistant(
  messages: ChatMessage[],
  row: ChatMessage,
  anchorId?: string | null,
): ChatMessage[] {
  const msgId = row.id;
  const existingIdx = messages.findIndex((m) => m.id === msgId);

  if (existingIdx >= 0) {
    const { isDelta: _d, deltaType: _t, ...base } = messages[existingIdx];
    const updated: ChatMessage = { ...base, ...row };
    if (!anchorId) {
      return messages.map((m) => (m.id === msgId ? updated : m));
    }
    const anchorIdx = messages.findIndex((m) => m.id === anchorId);
    if (anchorIdx < 0) {
      return messages.map((m) => (m.id === msgId ? updated : m));
    }
    const targetIdx = anchorIdx + 1;
    if (existingIdx === targetIdx) {
      return messages.map((m) => (m.id === msgId ? updated : m));
    }
    const without = messages.filter((m) => m.id !== msgId);
    const newAnchorIdx = without.findIndex((m) => m.id === anchorId);
    if (newAnchorIdx < 0) {
      return [...without, updated];
    }
    return [
      ...without.slice(0, newAnchorIdx + 1),
      updated,
      ...without.slice(newAnchorIdx + 1),
    ];
  }

  if (!anchorId) {
    return [...messages, row];
  }

  const anchorIdx = messages.findIndex((m) => m.id === anchorId);
  if (anchorIdx < 0) {
    return [...messages, row];
  }

  return [...messages.slice(0, anchorIdx + 1), row, ...messages.slice(anchorIdx + 1)];
}
